import { useMemo } from "react";

import { UseQueryResult } from "@tanstack/react-query";

import { Role, useMyRoleOnEngagement, useMyRoleOnProject, usePermissionsByRole } from "../api";
import {
    AppPermissions,
    AreaPermissions,
    SecurityArea,
    PermissionsByAreaType,
    Permission,
    roleCanSeeArea,
    roleHasPermission,
    roleHasAnyPermission,
} from "../auth";

const emptyPermissions = {};

export function useEngagementAppPermissions(engagementId: string | null | undefined, enabled = true): AppPermissions {
    const myRoleQuery = useMyRoleOnEngagement(engagementId, enabled);
    return useAppPermissionsHelper(myRoleQuery);
}
export function useProjectAppPermissions(engagementId: string | null | undefined, projectId: string | null | undefined, enabled = true): AppPermissions {
    const myRoleQuery = useMyRoleOnProject(engagementId, projectId, enabled);
    return useAppPermissionsHelper(myRoleQuery);
}
function useAppPermissionsHelper(myRoleQuery: UseQueryResult<Role> | null, enabled = true): AppPermissions {
    const permissionsQuery = usePermissionsByRole(enabled);
    const permissionsByRole = permissionsQuery.data || emptyPermissions;

    return useMemo(() => {
        const permissions: AppPermissions = {
            loading: myRoleQuery?.isLoading || permissionsQuery.isLoading,
            isInternal: false,
            showArea: (area: SecurityArea) => roleCanSeeArea(permissionsByRole, area, myRoleQuery?.data?.id),
            has: (permission: Permission | null) => false,
            hasAny: (...permissions: Permission[]) => false,
        };

        if (myRoleQuery?.data?.id) {
            permissions.isInternal = myRoleQuery?.data?.isInternal;
            permissions.has = (permission: Permission | null) => roleHasPermission(permissionsByRole, permission, myRoleQuery?.data?.id);
            permissions.hasAny = (...permissions: Permission[]) => roleHasAnyPermission(permissionsByRole, permissions, myRoleQuery?.data?.id);
        }
        return permissions;
    }, [permissionsByRole, permissionsQuery.isLoading, myRoleQuery?.data?.id, myRoleQuery?.data?.isInternal, myRoleQuery?.isLoading]);
}

export function useEngagementAreaPermissions<A extends SecurityArea>(area: A, engagementId: string | null | undefined): AreaPermissions<A> {
    const myRoleQuery = useMyRoleOnEngagement(engagementId);
    return useAreaPermissions(area, myRoleQuery);
}
export function useProjectAreaPermissions<A extends SecurityArea>(
    area: A,
    engagementId: string | null | undefined,
    projectId: string | null | undefined,
): AreaPermissions<A> {
    const myRoleQuery = useMyRoleOnProject(engagementId, projectId);
    return useAreaPermissions(area, myRoleQuery);
}

function useAreaPermissions<A extends SecurityArea>(area: A, myRoleQuery: UseQueryResult<Role>): AreaPermissions<A> {
    const permissionsQuery = usePermissionsByRole();
    const permissionsByRole = permissionsQuery.data || emptyPermissions;

    return useMemo(() => {
        const permissions: AreaPermissions<A> = {
            loading: myRoleQuery.isLoading || permissionsQuery.isLoading,
            isInternal: false,
            showArea: roleCanSeeArea(permissionsByRole, area, myRoleQuery.data?.id),
            has: (permission: PermissionsByAreaType<A> | null) => false,
            hasAny: (...permissions: PermissionsByAreaType<A>[]) => false,
        };

        if (myRoleQuery.data?.id) {
            permissions.isInternal = myRoleQuery.data?.isInternal;
            permissions.has = (permission: PermissionsByAreaType<A> | null) => roleHasPermission(permissionsByRole, permission, myRoleQuery.data?.id);
            permissions.hasAny = (...permissions: PermissionsByAreaType<A>[]) => roleHasAnyPermission(permissionsByRole, permissions, myRoleQuery.data?.id);
        }
        return permissions;
    }, [myRoleQuery.isLoading, myRoleQuery.data?.id, myRoleQuery.data?.isInternal, permissionsQuery.isLoading, permissionsByRole, area]);
}
